import { APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, initFlatpickrInShadow, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';
import flatpickr from 'flatpickr';
import { format } from 'date-fns';

interface UpcomingEarningConfig extends WidgetConfig {
    dateRange?: string;
    onDateRangeChange?: (range: string) => void;
    availableDateRanges?: string[];
    enableCalendar?: boolean;
    onDateChange?: (startDate: string, endDate: string) => void;
}

export class TraderverseUpComingEarning {
    private container: HTMLElement;
    private config: UpcomingEarningConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private startDate: string = '';
    private endDate: string = '';
    private selectedRange: string = 'Next Week';
    private datePickerInstance: any = null;

    constructor(config: UpcomingEarningConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if (config.dateRange) {
            this.selectedRange = config.dateRange;
        }

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private setDateRange(range: string): void {
        const today = new Date();
        let start = new Date(today.getTime());
        let end = new Date(today.getTime());

        switch (range) {
            case 'Current Week':
                start.setDate(today.getDate() - today.getDay());
                end.setDate(today.getDate() + (6 - today.getDay()));
                break;
            case 'Next Week':
                start.setDate(today.getDate() + (7 - today.getDay()));
                end = new Date(start.getTime());
                end.setDate(end.getDate() + 6);
                break;
            case 'Last 2 Weeks':
                start.setDate(today.getDate() - 14);
                this.startDate = start.toISOString().split('T')[0];
                this.endDate = today.toISOString().split('T')[0];
                return;
            case 'Last 3 Months':
                start.setMonth(today.getMonth() - 3);
                this.startDate = start.toISOString().split('T')[0];
                this.endDate = today.toISOString().split('T')[0];
                return;
            default:
                start.setDate(today.getDate() - 5);
                this.startDate = start.toISOString().split('T')[0];
                this.endDate = today.toISOString().split('T')[0];
                return;
        }

        this.startDate = start.toISOString().split('T')[0];
        this.endDate = end.toISOString().split('T')[0];
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<UpcomingEarningConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            dateRange: 'Next Week',
            availableDateRanges: ['Current Week', 'Next Week', 'Last 2 Weeks', 'Last 3 Months'],
            enableCalendar: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

        this.setDateRange(this.selectedRange);
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        this.attachStyles();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Upcoming Earnings</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>

        <div class="inBody">
          <div class="optionsRow" data-testing-id="options-row">
            <div class="item date-range" data-testing-id="date-range-selector">
              <select class="range-select" data-testing-id="range-select">
                ${(this.config.availableDateRanges || ['Current Week', 'Next Week', 'Last 2 Weeks', 'Last 3 Months'])
                .map(range => `<option value="${range}"${range === this.selectedRange ? ' selected' : ''}>${range}</option>`)
                .join('')}
              </select>
            </div>
            <span></span>
            ${this.config.enableCalendar !== false ? `
            <div class="item date-picker" data-testing-id="date-picker-container">
              <input type="text" class="date-input" data-testing-id="date-input" />
            </div>
            ` : ''}
          </div>
          <div class="chartDv" id="eventsContainer" style="height: 100%; padding: 0; margin: 0;" data-testing-id="events-container"></div>
        </div>
      </div>
    `;

        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

        const rangeSelect = this.shadowRoot.querySelector('.range-select') as HTMLSelectElement;
        if (rangeSelect) {
            rangeSelect.addEventListener('change', () => {
                this.selectedRange = rangeSelect.value;
                this.setDateRange(this.selectedRange);
                this.config.onDateRangeChange?.(this.selectedRange);
                this.setupChart();
                this.initDatePicker();
            });
        }

        setTimeout(() => this.initDatePicker(), 0);
    }

    private initDatePicker(): void {
        try {
            const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
            if (!input) return;

            if (this.datePickerInstance && this.datePickerInstance.destroy) {
                this.datePickerInstance.destroy();
                this.datePickerInstance = null;
            }

            this.datePickerInstance = initFlatpickrInShadow(input, this.shadowRoot, {
                mode: 'range',
                dateFormat: 'Y-m-d',
                defaultDate: [this.startDate || new Date(), this.endDate || new Date()],
                onClose: (selectedDates: Date[]) => {
                    if (selectedDates.length === 2) {
                        const [start, end] = selectedDates;
                        this.startDate = format(start, 'yyyy-MM-dd');
                        this.endDate = format(end, 'yyyy-MM-dd');
                        this.setupChart();
                        this.config.onDateChange?.(this.startDate, this.endDate);
                    }
                },
            });
        } catch (err) {
            console.warn('Date picker init failed:', err);
        }
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async setupChart(): Promise<void> {
        try {
            const params = new URLSearchParams({
                start_date: this.startDate,
                end_date: this.endDate,
                offset: '0',
                limit: '100'
            });

            const response = await fetch(`${APIURL}/v1/discover/upcoming-earnings?${params.toString()}`, {
                method: "GET",
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error("Failed to fetch data");
            this.currentData = await response.json();

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    }

    private filterByCountry(): void {
        if (!this.currentData?.data) return;
        let filtered = this.currentData.data;
        this.renderFilteredEvents(filtered);
    }

    private groupByReportDate(eventsData: any[]): Map<string, any[]> {
        const map = new Map<string, any[]>();
        for (const item of eventsData) {
            const key = item.report_date || '';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(item);
        }
        const sortedDates = Array.from(map.keys()).filter(Boolean).sort();
        const ordered = new Map<string, any[]>();
        for (const d of sortedDates) ordered.set(d, map.get(d)!);
        for (const d of map.keys()) { if (!d) ordered.set(d, map.get(d)!); }
        return ordered;
    }

    private formatReportDateHeader(dateStr: string): string {
        if (!dateStr) return dateStr;
        try {
            const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
            return format(d, 'EEEE, MMMM dd, yyyy');
        } catch {
            return dateStr;
        }
    }

    private formatEarningsTime(value: unknown): string {
        if (value == null || value === '') return '—';
        const raw = String(value).trim();
        if (!raw) return '—';

        // ISO / datetime strings → display time only (date is already in the group header)
        if (/^\d{4}-\d{2}-\d{2}/.test(raw) || raw.includes('T')) {
            try {
                const d = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
                if (!isNaN(d.getTime())) return format(d, 'h:mm a');
            } catch {
                /* fall through */
            }
        }

        return raw;
    }

    /** Map API exchange names to media.traderverse.io equity logo suffixes. */
    private getEquityLogoExchange(exchange?: string): string {
        const ex = String(exchange || '').trim().toLowerCase();
        if (!ex) return 'NASDAQ';
        if (ex.includes('otc')) return 'OTC';
        if (ex.includes('arca')) return 'AMEX';
        if (ex.includes('amex') || ex.includes('american')) return 'AMEX';
        if (ex.includes('new york') || ex.includes('nyse')) return 'NYSE';
        if (ex.includes('nasdaq capital')) return 'NASDAQCM';
        if (ex.includes('nasdaq')) return 'NASDAQ';
        return 'NASDAQ';
    }

    private getSymbolLogoUrl(symbol?: string, exchange?: string): string {
        const sym = String(symbol || '').toUpperCase();
        const ex = this.getEquityLogoExchange(exchange);
        return `https://media.traderverse.io/assets/equity/${sym}_${ex}.svg`;
    }

    private renderFilteredEvents(eventsData: any[]): void {
        const container = this.shadowRoot.querySelector('#eventsContainer');
        if (!container) return;

        if (!eventsData || eventsData.length === 0) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const byDate = this.groupByReportDate(eventsData);
        let html = `<div class="earnings-wrapper" data-testing-id="earnings-wrapper">`;
        
        let globalRowIndex = 0;
        let groupIndex = 0;
        
        for (const [reportDate, items] of byDate) {
            const dateLabel = reportDate ? this.formatReportDateHeader(reportDate) : 'No date';

            html += `
        <div class="earnings-date-group" data-testing-id="earnings-group-${groupIndex}" data-date="${reportDate || ''}" data-group-index="${groupIndex}">
          <div class="earnings-date-header" data-testing-id="earnings-date-header-${groupIndex}" data-group="${groupIndex}">${dateLabel}</div>
          <table class="earnings-table general-table" data-testing-id="earnings-table-${groupIndex}" data-group="${groupIndex}">
            <thead>
              <tr data-testing-id="table-header-${groupIndex}">
                <th data-testing-id="header-symbol">SYMBOL</th>
                <th data-testing-id="header-est-revenue">EST. REVENUE</th>
                <th data-testing-id="header-est-eps">EST. EPS</th>
                <th data-testing-id="header-time">TIME</th>
              </tr>
            </thead>
            <tbody>
            `;
            
            let rowIndex = 0;
            for (const item of items) {
                const symbolLogo = this.getSymbolLogoUrl(item.symbol, item.exchange);
                const estRevenue = item.estimated_revenue != null
                    ? (typeof item.estimated_revenue === 'number' ? `$${(item.estimated_revenue / 1e6).toFixed(2)}M` : String(item.estimated_revenue))
                    : 'N/A';
                const estEps = item.eps_estimate ?? '-';
                const time = this.formatEarningsTime(
                    item.when_earnings ?? item.earnings_time ?? item.time ?? item.last_updated_at
                );
                
                html += `
              <tr data-testing-id="earnings-row-${globalRowIndex}" 
                  data-symbol="${item.symbol || ''}" 
                  data-group="${groupIndex}" 
                  data-group-row="${rowIndex}"
                  data-global-index="${globalRowIndex}"
                  data-report-date="${reportDate || ''}"
                  data-est-revenue="${estRevenue}"
                  data-est-eps="${estEps}"
                  data-time="${time}">
                <td class="color-white" data-testing-id="cell-symbol"><img src="${symbolLogo}" alt="${item.symbol || ''}" style="width:24px;height:24px;border-radius:4px;vertical-align:middle;margin-right:6px;object-fit:contain;" /><strong>${item.symbol || '-'}</strong></td>
                <td data-testing-id="cell-est-revenue">${estRevenue}</td>
                <td data-testing-id="cell-est-eps">${estEps}</td>
                <td data-testing-id="cell-time">${time}</td>
              </tr>
                `;
                rowIndex++;
                globalRowIndex++;
            }
            
            html += `
            </tbody>
          </table>
        </div>
        `;
            groupIndex++;
        }
        
        html += `</div>`;
        container.innerHTML = html;
    }

private updateDisplay(): void {
    const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;

    const hasData =
        this.currentData?.data &&
        Array.isArray(this.currentData.data) &&
        this.currentData.data.length > 0;

    if (!hasData) {
        const container = this.shadowRoot.querySelector('#eventsContainer') as HTMLElement;

        if (container) {
            container.innerHTML = getNoDataHTML();
        }

        // ❌ remove testing id if no data
        chartDv?.removeAttribute('data-testing-id');
        return;
    }

    // render UI only when data exists
    this.filterByCountry();

    // ✅ only add testing id when UI is actually valid
    const renderedContainer = this.shadowRoot.querySelector('#eventsContainer');

    const isActuallyRendered =
        renderedContainer &&
        renderedContainer.innerHTML.trim().length > 0 &&
        !renderedContainer.innerHTML.includes('No upcoming earnings available');

    if (isActuallyRendered && chartDv) {
        chartDv.setAttribute('data-testing-id', 'UpComingEarning');
    } else {
        chartDv?.removeAttribute('data-testing-id');
    }
}
    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.config.symbol = symbol;
        this.setupChart();
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;
      
        this.config.theme = toggleWidgetTheme(
          this.shadowRoot,
          theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
      
        this.updateDisplay();
    }

    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        if (this.datePickerInstance && this.datePickerInstance.destroy) {
            try { this.datePickerInstance.destroy(); } catch (e) { /* ignore */ }
            this.datePickerInstance = null;
        }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseUpComingEarning: typeof TraderverseUpComingEarning; } }
if (typeof window !== 'undefined') { window.TraderverseUpComingEarning = TraderverseUpComingEarning; }